import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { useWelcomeOffer } from './WelcomeOfferContext';
import { apiClient } from '@/lib/api-client';
import { paymentStore } from '@/stores/paymentStore';
import { storage } from '@/lib/storage';

const shouldSkipGeoComply = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (storage.getItem('SKIP_GEOCOMPLY') === 'true') return true;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

interface OneTimeOfferContextType {
  showOneTimeOfferModal: boolean;
  setShowOneTimeOfferModal: (show: boolean) => void;
  currentOneTimeOffer: ScheduledOffer | null;
  currentOneTimeGroup: ScheduledOfferGroup | null;
  hasMoreOneTimeOffers: boolean;
  dismissCurrentOffer: () => void;
  checkAndShowOneTimeOffer: () => Promise<void>;
}

const OneTimeOfferContext = createContext<OneTimeOfferContextType | undefined>(undefined);

export const useOneTimeOffer = () => {
  const context = useContext(OneTimeOfferContext);
  if (context === undefined) {
    throw new Error('useOneTimeOffer must be used within a OneTimeOfferProvider');
  }
  return context;
};

const ONE_TIME_OFFER_SHOWN_KEY = 'one_time_offer_shown_session';

export const OneTimeOfferProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, isGeoComplyCompliant } = useAuth();
  const { requestShow, notifyDismissed, shouldShow, hasHigherPriorityModal } = useModalQueue();
  const { showWelcomeOfferModal } = useWelcomeOffer();
  
  const [oneTimeGroupsQueue, setOneTimeGroupsQueue] = useState<ScheduledOfferGroup[]>([]);
  const [oneTimeOffersQueue, setOneTimeOffersQueue] = useState<ScheduledOffer[]>([]);
  
  const [currentOneTimeGroup, setCurrentOneTimeGroup] = useState<ScheduledOfferGroup | null>(null);
  const [currentOneTimeOffer, setCurrentOneTimeOffer] = useState<ScheduledOffer | null>(null);
  
  const [hasChecked, setHasChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
  
  const hasMoreOneTimeOffers = oneTimeGroupsQueue.length > 0 || oneTimeOffersQueue.length > 0;

  const loadNextOffer = useCallback(() => {
    if (oneTimeGroupsQueue.length > 0) {
      const [nextGroup, ...remainingGroups] = oneTimeGroupsQueue;
      setCurrentOneTimeGroup(nextGroup);
      setCurrentOneTimeOffer(null);
      setOneTimeGroupsQueue(remainingGroups);
      return true;
    }
    
    if (oneTimeOffersQueue.length > 0) {
      const [nextOffer, ...remainingOffers] = oneTimeOffersQueue;
      setCurrentOneTimeOffer(nextOffer);
      setCurrentOneTimeGroup(null);
      setOneTimeOffersQueue(remainingOffers);
      return true;
    }
    
    sessionStorage.setItem(ONE_TIME_OFFER_SHOWN_KEY, 'true');
    setIsEligible(false);
    return false;
  }, [oneTimeGroupsQueue, oneTimeOffersQueue]);
  
  const dismissCurrentOffer = useCallback(() => {
    setIsTemporarilyHidden(false);
    setCurrentOneTimeGroup(null);
    setCurrentOneTimeOffer(null);
    notifyDismissed('ONE_TIME_OFFER');
    
    setTimeout(() => {
      if (loadNextOffer()) {
        if (!paymentStore.getState().isOpen) {
          requestShow('ONE_TIME_OFFER');
        }
      }
    }, 100);
  }, [notifyDismissed, loadNextOffer, requestShow]);
  
  useEffect(() => {
    let wasOpen = paymentStore.getState().isOpen;
    
    const unsubscribe = paymentStore.subscribe(() => {
      const isOpen = paymentStore.getState().isOpen;
      
      if (wasOpen && !isOpen && isEligible && (currentOneTimeOffer || currentOneTimeGroup)) {
        requestShow('ONE_TIME_OFFER');
      }
      
      wasOpen = isOpen;
    });
    
    return unsubscribe;
  }, [isEligible, currentOneTimeOffer, currentOneTimeGroup, requestShow]);

  const checkAndShowOneTimeOffer = useCallback(async () => {
    if (!user || !session) return;
    if (!user.email_verified) return;

    const hasShownThisSession = sessionStorage.getItem(ONE_TIME_OFFER_SHOWN_KEY);
    if (hasShownThisSession) return;

    try {
      const data = await apiClient.get('/coins/packages');
      
      if (!data || (!data.scheduledOffers && !data.success)) return;

      const scheduledOffers = data.scheduledOffers || { groups: [], ungrouped: [] };
      
      const oneTimeUngrouped = (scheduledOffers.ungrouped || []).filter(
        (offer: ScheduledOffer) => 
          offer.cadence_type === 'one_time' && 
          !offer.is_welcome_offer && 
          offer.can_purchase !== false && 
          (offer.purchases_remaining ?? 1) > 0
      );
      
      const oneTimeGrouped = (scheduledOffers.groups || []).filter(
        (group: ScheduledOfferGroup) => {
          const hasOneTimeOffer = group.offers.some(
            (o: ScheduledOffer) => o.cadence_type === 'one_time' && !o.is_welcome_offer
          );
          const hasPurchasableOffer = group.offers.some(
            (o: ScheduledOffer) => o.can_purchase !== false && (o.purchases_remaining ?? 1) > 0
          );
          return hasOneTimeOffer && hasPurchasableOffer && !group.is_welcome_group;
        }
      );

      if (oneTimeUngrouped.length > 0 || oneTimeGrouped.length > 0) {
        setIsEligible(true);
        
        if (oneTimeGrouped.length > 0) {
          const [firstGroup, ...remainingGroups] = oneTimeGrouped;
          setCurrentOneTimeGroup(firstGroup);
          setCurrentOneTimeOffer(null);
          setOneTimeGroupsQueue(remainingGroups);
          setOneTimeOffersQueue(oneTimeUngrouped);
        } else if (oneTimeUngrouped.length > 0) {
          const [firstOffer, ...remainingOffers] = oneTimeUngrouped;
          setCurrentOneTimeOffer(firstOffer);
          setCurrentOneTimeGroup(null);
          setOneTimeOffersQueue(remainingOffers);
        }
        
        if (!paymentStore.getState().isOpen && !hasHigherPriorityModal('ONE_TIME_OFFER')) {
          requestShow('ONE_TIME_OFFER');
        }
      }
    } catch {
      // Silently fail
    }
  }, [user, session, requestShow, hasHigherPriorityModal]);

  useEffect(() => {
    const geoComplyPassed = shouldSkipGeoComply() || isGeoComplyCompliant === true;
    
    if (user && session && user.email_verified && geoComplyPassed && !hasChecked && !showWelcomeOfferModal) {
      checkAndShowOneTimeOffer();
      setHasChecked(true);
    } else if (!user) {
      setHasChecked(false);
      setIsEligible(false);
      setOneTimeGroupsQueue([]);
      setOneTimeOffersQueue([]);
      setCurrentOneTimeGroup(null);
      setCurrentOneTimeOffer(null);
      if (sessionStorage.getItem(ONE_TIME_OFFER_SHOWN_KEY)) {
        sessionStorage.removeItem(ONE_TIME_OFFER_SHOWN_KEY);
      }
    }
  }, [user, session, isGeoComplyCompliant, hasChecked, checkAndShowOneTimeOffer, showWelcomeOfferModal]);

  const queueShowsModal = shouldShow('ONE_TIME_OFFER');
  const showOneTimeOfferModal = queueShowsModal && !isTemporarilyHidden;

  const setShowOneTimeOfferModal = useCallback((show: boolean) => {
    if (show) {
      setIsTemporarilyHidden(false);
    } else {
      setIsTemporarilyHidden(true);
    }
  }, []);

  const value = {
    showOneTimeOfferModal,
    setShowOneTimeOfferModal,
    currentOneTimeOffer,
    currentOneTimeGroup,
    hasMoreOneTimeOffers,
    dismissCurrentOffer,
    checkAndShowOneTimeOffer,
  };

  return (
    <OneTimeOfferContext.Provider value={value}>
      {children}
    </OneTimeOfferContext.Provider>
  );
};
